import { Modal } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ICountryGroup } from '~/src/dash/api/console-api';
import {
  generateCountryGroupOptionsList,
  getCountryGroupsByPublisherID,
  updateCountryGroup,
} from '~/src/dash/api/country-group';
import { ISelectOption, ISelectPreset } from '~/src/dash/components/dropdowns/common';
import { IndependentSelect } from '~/src/dash/components/dropdowns/IndependentSelect';
import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import {
  removeEmojiPrefixFromLabel,
  validateName,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import { DeleteRegionDialog } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/RegionsEditor/DeleteRegionDialog';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  TextButton,
  TextInput,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const labelWidth = 108;
const rowHeight = 40;

interface IProps {
  handleSuccess: () => void;
  handleCancel: () => void;
  handleDelete: (region: ICountryGroup) => void;
  isDisabled?: boolean;
  commonRegionGroups: ISelectPreset[];
}

export const EditRegionModal = ({
  handleDelete,
  handleSuccess,
  handleCancel,
  commonRegionGroups,
  isDisabled = false,
}: IProps) => {
  const { session } = useBaseStore();
  const { lists } = useFormBuilderStore();

  const [regions, setRegions] = useState<ICountryGroup[]>([]);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [regionOptions, setRegionOptions] = useState<ISelectOption[]>(lists.CountryGroups);
  const [selectedRegionID, setSelectedRegionID] = useState<Set<number>>(new Set([]));
  const [selectedRegion, setSelectedRegion] = useState<ICountryGroup>({} as ICountryGroup);
  const [reactCounter, setReactCounter] = useState(1);
  const [errors, setErrors] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [selectedCountryIDs, setSelectedCountryIDs] = useState<Set<string>>(new Set([]));
  const [open, setOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadRegions();
    return () => {
      setErrors([]);
      setSuccessMessage('');
    };
  }, []);

  useEffect(() => {
    if (selectedRegionID.size > 0) {
      const selectedID = Array.from(selectedRegionID)[0];
      const selectedCountryGroup = regions.find((option) => option.ID === selectedID);
      if (selectedCountryGroup) {
        unstable_batchedUpdates(() => {
          setSelectedRegion(selectedCountryGroup);
          setName(selectedCountryGroup.Name);
          setSuccessMessage('');
          setErrors([]);
          setSelectedCountryIDs(
            new Set(
              selectedCountryGroup.CountryIsos
                ? selectedCountryGroup.CountryIsos.split(',').map((country) =>
                    country.toLowerCase(),
                  )
                : [],
            ),
          );
          setReactCounter(reactCounter + 1);
        });
      } else {
        unstable_batchedUpdates(() => {
          setSelectedRegion({} as ICountryGroup);
          setName('');
          setSuccessMessage('');
          setSelectedCountryIDs(new Set([]));
          setReactCounter(reactCounter + 1);
        });
      }
    }
  }, [selectedRegionID]);

  const loadRegions = () => {
    getCountryGroupsByPublisherID(session.publisher.ID)
      .then((countryGroups) => {
        setRegions(countryGroups || []);
      })
      .catch((err) => {
        console.error(err);
        setRegions([]);
      });
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    unstable_batchedUpdates(() => {
      setErrors([]);
      setSuccessMessage('');
      setName('');
      setSelectedCountryIDs(new Set([]));
      setOpen(false);
      if (hasUpdate) {
        handleSuccess();
      } else {
        handleCancel();
      }
    });
  };

  const handleChangeName = (text: string) => {
    unstable_batchedUpdates(() => {
      setName(text);
      setIsSaveDisabled(false);
    });
  };

  const handleApplyRegion = (value: Set<number>) => {
    unstable_batchedUpdates(() => {
      setSelectedRegionID(value);
      setIsSaveDisabled(true);
    });
  };

  const handleApplyCountries = (value: Set<string>) => {
    unstable_batchedUpdates(() => {
      setSelectedCountryIDs(value);
      setIsSaveDisabled(false);
    });
  };

  const handleSave = () => {
    if (isSaveDisabled) {
      return;
    } else {
      setIsSaveDisabled(true);
    }
    // validations
    const errorMessages: string[] = [];
    // validate name
    const { sanitizedName, nameError } = validateName(name);
    if (nameError) {
      errorMessages.push(nameError);
    }
    // validate there is at least one country selected
    if (isEmpty(selectedCountryIDs)) {
      errorMessages.push('No countries selected.');
    }
    if (!isEmpty(errorMessages)) {
      setErrors(errorMessages);
      return;
    }

    updateCountryGroup({
      ID: selectedRegion.ID,
      PublisherID: Number(session.publisher.ID),
      Name: sanitizedName,
      CountryIsos: Array.from(selectedCountryIDs)
        .map((country) => country.toUpperCase())
        .join(','),
    } as ICountryGroup).then((cg) => {
      if (!isEmpty(cg) && cg.ID !== 0) {
        let newOptions: ISelectOption[] = [];
        generateCountryGroupOptionsList(session)
          .then((cgs) => {
            if (cgs) {
              newOptions = cgs;
            }
            unstable_batchedUpdates(() => {
              setRegionOptions(newOptions);
              setHasUpdate(true);
              setReactCounter(reactCounter + 1);
              setSuccessMessage(`You have successfully updated Region: ${cg.Name}`);
              loadRegions();
              setIsSaveDisabled(true);
            });
          })
          .catch((err) => {
            console.error(err);
            unstable_batchedUpdates(() => {
              setRegionOptions(newOptions);
              setReactCounter(reactCounter + 1);
              setSuccessMessage('');
              setErrors(['An error has occurred while updating. Please try again.']);
              setIsSaveDisabled(false);
            });
          });
      }
    });
  };

  if (isEmpty(regions)) return null;

  return (
    <div>
      <TextButton size='small' onClick={handleOpen} disabled={isDisabled}>
        Edit Region
      </TextButton>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='edit-region-modal'
        aria-describedby='edit-region-modal'
      >
        <Panel
          highlighted
          smallVerticalPadding
          extraLargeHorizontalPadding
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '720px',
          }}
        >
          <Rows>
            <Row noHorizontalPadding verticalPadding>
              <Text large bold>
                Edit Region
              </Text>
            </Row>
            {/* ERRORS */}
            {!isEmpty(errors) && (
              <Row style={{ padding: '0px 20px 24px' }}>
                <Rows>
                  {errors.map((error) => (
                    <Row noPadding>
                      <Text small color={publicaColors.red}>
                        <Span style={{ paddingRight: spacing.small }}>•</Span>
                        {error}
                      </Text>
                    </Row>
                  ))}
                </Rows>
              </Row>
            )}
            {/* FORMS */}
            <Row noPadding>
              <Rows gap={spacing.small} style={{ alignItems: 'center', justifyContent: 'center' }}>
                <Row noPadding height={rowHeight}>
                  <Cols gap={spacing.large} style={{ alignItems: 'center' }}>
                    <Col width={labelWidth}>
                      <Text small bold>
                        Region
                      </Text>
                    </Col>
                    <Col flex style={{ width: '480px' }}>
                      <IndependentSelect
                        enableLabelTooltip
                        handleApplySelect={handleApplyRegion}
                        listLabel={'Regions'}
                        options={regionOptions}
                        placeholder='Select Region'
                        selectedOptionIDs={selectedRegionID}
                        selectedLimit={1}
                        singularLabel='Region'
                        key={reactCounter}
                        maxWidth={480}
                        isSelectionRequired
                      />
                    </Col>
                  </Cols>
                </Row>
                {!isEmpty(selectedRegion) && (
                  <>
                    <Row noPadding height={rowHeight}>
                      <Cols gap={spacing.large} style={{ alignItems: 'center' }}>
                        <Col width={labelWidth}>
                          <Text small bold>
                            Name
                          </Text>
                        </Col>
                        <Col flex style={{ width: '480px' }}>
                          <TextInput
                            placeholder='New Region'
                            minLength={3}
                            maxLength={99}
                            isAutoFocused
                            handleOnChange={handleChangeName}
                            inputSize='small'
                            width='480px'
                            initialValue={name}
                            key={selectedRegion.ID}
                            inputID='region_name'
                          />
                        </Col>
                      </Cols>
                    </Row>
                    <Row noPadding height={rowHeight}>
                      <Cols gap={spacing.large} style={{ alignItems: 'center' }}>
                        <Col width={labelWidth}>
                          <Text small bold>
                            Countries
                          </Text>
                        </Col>
                        <Col flex style={{ width: '480px' }}>
                          <IndependentSelect
                            enableLabelTooltip
                            handleApplySelect={handleApplyCountries}
                            listLabel='Countries'
                            options={lists.Countries}
                            placeholder='Select Countries'
                            selectedOptionIDs={selectedCountryIDs}
                            singularLabel='Country'
                            key={selectedRegion.ID}
                            maxWidth={480}
                            extractStringToSort={removeEmojiPrefixFromLabel}
                            presets={commonRegionGroups}
                          />
                        </Col>
                      </Cols>
                    </Row>
                  </>
                )}
              </Rows>
            </Row>
            {successMessage && (
              <Row noHorizontalPadding verticalPadding style={{ textAlign: 'center' }}>
                <Text small color={publicaColors.darkGreen}>
                  {successMessage}
                </Text>
              </Row>
            )}
            <Row noHorizontalPadding verticalPadding>
              <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Col>
                  {selectedRegionID.size > 0 && (
                    <DeleteRegionDialog region={selectedRegion} handleSuccess={handleDelete} />
                  )}
                </Col>
                <Col>
                  <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                    <Col>
                      <ButtonV2 small onClick={handleClose}>
                        Close
                      </ButtonV2>
                    </Col>
                    {selectedRegionID.size > 0 && (
                      <Col>
                        <ButtonV2 small onClick={handleSave} disabled={isSaveDisabled}>
                          Save
                        </ButtonV2>
                      </Col>
                    )}
                  </Cols>
                </Col>
              </Cols>
            </Row>
          </Rows>
        </Panel>
      </Modal>
    </div>
  );
};
